import { Connection, ISubscription, SubscribeHandler } from 'autobahn';
import autobahn from 'autobahn-browser';
import { makeAutoObservable, reaction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { isArray, logger, queryAPI, storageAPI } from '@cutwise/utils';

import { KEY_TOKEN } from '@/dmdrive/core/constants/storage';
import { ConferenceState } from '@/dmdrive/topic/constants/topic';
import { Channel, ChannelModel } from '@/dmdrive/topic/entities/Channel';
import { TopicModel } from '@/dmdrive/topic/entities/Topic';
import { NotifyService } from '@/dmdrive/topic/services/NotifyService';

import { PointerData } from '@carbon/dm-industrial/lib/dm-industrial';

import { TopicStore } from './TopicStore';
import { PointerChangeData, StateChangeData, SystemChangeData } from './TopicWSStore.types';

export class TopicWSStore {
  private connection: Nullable<Connection> = null;

  private channels: Nullable<Channel[]> = null;

  private subscriptions: Record<string, ISubscription<never>> = {};

  private pointers: Record<string, PointerData> = {};

  private pingTicker!: NodeJS.Timeout;

  private readonly topicStore: TopicStore;

  constructor(store: TopicStore) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.topicStore = store;

    reaction(
      () => this.topicStore.topic?.onlineUsers,
      (value) => {
        if (value && value.length > 0) {
          this.getChannels();
        }
      },
    );

    reaction(
      () => this.stateChannel,
      () => this.listenToStateChannel(),
    );

    reaction(
      () => this.systemChannel,
      () => this.listenToSystemChannel(),
    );

    reaction(
      () => [this.topicStore.topic?.topicUsers, this.watchedPointerChannels],
      () => this.listenToPointersChannels(),
    );
  }

  private get pointerChannel() {
    return this.channels?.find((channel) => channel.type === 'pointer' && channel.userURN === this.topicStore.me?.urn);
  }

  private get systemChannel() {
    return this.channels?.find((channel) => channel.type === 'system');
  }

  private get stateChannel() {
    return this.channels?.find((channel) => channel.type === 'state');
  }

  private get watchedPointerChannels() {
    return (
      this.channels?.filter((channel) => {
        if (channel.type !== 'pointer') {
          return false;
        }

        return channel.userURN !== this.topicStore.me?.urn;
      }) ?? []
    );
  }

  private get isDebug() {
    return queryAPI.has('debug');
  }

  *openTopic() {
    yield this.connect();
    yield this.getTopic();
    yield this.getChannels();

    if (!this.isDebug) {
      this.pingTicker = setInterval(() => this.pingTopic(), 5000);
    }
  }

  close() {
    clearInterval(this.pingTicker);

    try {
      Object.keys(this.subscriptions).forEach((key) => {
        this.subscriptions[key].unsubscribe();
      });

      this.subscriptions = {};

      this.connection?.session?.call('leaveTopic', [this.topicStore.topic?.urn]);

      this.connection?.close?.();
    } catch (e) {
      logger.error(e);
    }

    this.connection = null;
  }

  promoteToMaster(userURN: Nullable<string>) {
    if (!userURN) {
      return;
    }

    return this.connection?.session?.call('promoteToMaster', [this.topicStore.topic?.urn, userURN], {});
  }

  demoteMaster() {
    if (!this.topicStore.topic?.owner?.urn) {
      return;
    }

    this.promoteToMaster(this.topicStore.topic?.owner?.urn);
  }

  requestForState() {
    if (!this.stateChannel) {
      return;
    }

    this.publishTo(this.stateChannel.urn, 'requestMasterState');
  }

  requestForMaster() {
    return this.connection?.session?.call('requestForMaster', [this.topicStore.topic?.urn], {});
  }

  cancelRequestForMaster() {
    return this.connection?.session?.call('cancelRequestForMaster', [this.topicStore.topic?.urn], {});
  }

  publishPointers(data: unknown) {
    if (!this.pointerChannel) {
      return;
    }

    if (!this.isDebug) {
      this.publishTo(this.pointerChannel?.urn, data);
    }
  }

  publishState(data: unknown) {
    if (!this.stateChannel) {
      return;
    }

    if (!this.topicStore.me?.isMaster) {
      return;
    }

    this.publishTo(this.stateChannel?.urn, data);
  }

  publishSystem(data: unknown) {
    if (!this.systemChannel) {
      return;
    }

    this.publishTo(this.systemChannel?.urn, data);
  }

  private connect() {
    return new Promise<void>((resolve, reject) => {
      try {
        this.connection = new autobahn.Connection({
          url: process.env.DMCLOUD_WS_SERVER,
          realm: 'dmcloud',
          authmethods: ['cutwise-token'],
          authid: storageAPI.get(KEY_TOKEN),
        });

        this.connection!.onopen = () => {
          resolve();
        };

        this.connection!.onclose = (status) => {
          reject(new Error(status));

          return false;
        };

        this.connection!.open();
      } catch (e) {
        reject(e);
      }
    });
  }

  private *getTopic() {
    if (!this.connection?.session) {
      return;
    }

    const topic: { kwargs: TopicModel } = yield this.connection.session.call<{ kwargs: TopicModel }>('openTopic', [
      this.topicStore.topic?.urn,
    ]);

    if (!isArray(topic?.kwargs.topicUsers)) {
      return;
    }

    this.topicStore.topic?.setOnlineUsers(topic.kwargs.topicUsers);
  }

  private *getChannels() {
    if (!this.topicStore.topic) {
      return;
    }

    const channels: { kwargs: ChannelModel } = yield this.connection?.session?.call('listChannels', [
      this.topicStore.topic!.urn,
    ]);

    if (!channels?.kwargs) {
      return;
    }

    this.channels = Object.values(channels.kwargs).map((channel) => {
      const conferenceChannel = new Channel(channel);
      const topicUser = this.topicStore.topic!.topicUsers.find(({ urn }) => urn === conferenceChannel.userURN);

      if (topicUser) {
        conferenceChannel.setUser(topicUser);
      }

      return conferenceChannel;
    });

    // create viewer pointer channel
    if (!this.pointerChannel && this.topicStore.me?.urn) {
      yield this.registerChannel(this.topicStore.topic!.urn, {
        type: 'pointer',
        userURN: this.topicStore.me.urn,
      });

      this.getChannels();
    }
  }

  private registerChannel(topicUrn: string, meta: ChannelModel['meta']) {
    const urn = `urn:channel:${uuidv4()}`;

    return this.connection?.session
      ?.call('createChannel', [topicUrn, urn], meta)
      .then(() => new Channel({ urn, meta }));
  }

  private *listenToStateChannel() {
    if (!this.stateChannel) {
      return;
    }

    yield this.subscribeTo(this.stateChannel, (state?: StateChangeData[]) => {
      if (!state) {
        return;
      }

      const [data] = state;

      if (this.topicStore.conferenceState === ConferenceState.Explore) {
        return;
      }

      if ((data as unknown as string) === 'requestMasterState' && this.topicStore.me?.isMaster) {
        this.publishState(this.topicStore.settings);

        return;
      }

      this.topicStore.topicViewerStore.applyWebSocketState(data);
    });
  }

  private *listenToSystemChannel() {
    if (!this.systemChannel) {
      return;
    }

    yield this.subscribeTo(this.systemChannel, (data, state: SystemChangeData) => {
      const message = data[0];

      if (message === 'topicUpdate') {
        this.topicStore.getTopic(this.topicStore.topic!.id);
      }

      if (isArray(state.topic?.topicUsers)) {
        this.topicStore.topic?.setOnlineUsers(state.topic.topicUsers);
      }
    });
  }

  private listenToPointersChannels() {
    this.watchedPointerChannels.forEach((ch) => {
      this.subscribeTo(ch, (args?: PointerChangeData) => {
        if (!args) {
          return;
        }

        const user = ch.user;

        if (user?.urn) {
          const [x, y, z, pointer] = args;

          this.pointers[user.urn] = {
            urn: user.urn,
            name: user.username,
            color: user.color,
            x,
            y,
            z,
            pointer: pointer.urn,
          };

          if (!this.topicStore.isJoinedConference) {
            return;
          }

          this.topicStore.topicViewerStore.viewer?.setPointers(Object.values(this.pointers));
        }
      });
    });
  }

  private *subscribeTo(channel: Channel, cb: SubscribeHandler<Any>) {
    if (!this.connection?.session) {
      return;
    }

    try {
      this.unsubscribe(channel);

      this.subscriptions[channel.urn] = yield this.connection.session.subscribe(channel.urn, cb);
    } catch (e) {
      NotifyService.error(e);
    }
  }

  private unsubscribe(channel: Channel) {
    if (!this.connection?.session) {
      return;
    }

    try {
      this.subscriptions[channel.urn]?.unsubscribe();

      delete this.subscriptions[channel.urn];
    } catch (e) {
      NotifyService.error(e);
    }
  }

  private publishTo(urn: string, data: unknown) {
    if (!this.connection?.session) {
      return;
    }

    let message: unknown[] = [];

    if (Array.isArray(data)) {
      message = message.concat(...data);
    } else {
      message.push(data);
    }

    message.push({
      urn: this.topicStore.topic?.urn,
    });

    this.connection.session.publish(urn, message);
  }

  private pingTopic() {
    return this.connection?.session?.call('pingTopic', [this.topicStore.topic?.urn]);
  }
}
